import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle, XCircle, AlertTriangle, SkipForward, Play, RefreshCw,
  ChevronDown, ChevronRight, Radio, Shield, ArrowLeft
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "wouter";

type QaStatus = "pass" | "fail" | "warn" | "skipped";

/** Shape returned by GET /api/qa/tests → latestResult */
interface QaResult {
  id: number;
  testKey: string;
  category: string;
  testName: string;
  status: QaStatus;
  message: string;
  durationMs: number;
  isLive: boolean;
  ranAt: string;
}

/** Shape streamed live by POST /api/qa/run and POST /api/qa/run/:key */
interface LiveRunResult {
  key: string;
  category: string;
  name: string;
  isLive: boolean;
  status: QaStatus;
  message: string;
  durationMs: number;
  ranAt: string;
}

/** Unified display shape — only the fields needed for rendering */
interface DisplayResult {
  status: QaStatus;
  message: string;
  durationMs: number;
  ranAt: string;
}

function toDisplayResult(r: QaResult | LiveRunResult): DisplayResult {
  return { status: r.status, message: r.message, durationMs: r.durationMs, ranAt: r.ranAt };
}

interface QaTest {
  key: string;
  category: string;
  name: string;
  isLive: boolean;
  latestResult: QaResult | null;
}

interface HistoryEntry {
  id: number;
  status: QaStatus;
  message: string;
  durationMs: number;
  ranAt: string;
}

const STATUS_CONFIG: Record<QaStatus, { icon: typeof CheckCircle; color: string; bg: string; label: string }> = {
  pass: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", label: "Pass" },
  fail: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", label: "Fail" },
  warn: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", label: "Warn" },
  skipped: { icon: SkipForward, color: "text-foreground/30", bg: "bg-foreground/5 border-foreground/10", label: "Skip" },
};

function StatusBadge({ status, size = "sm" }: { status: QaStatus; size?: "sm" | "xs" }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.color}`}>
      <Icon size={size === "xs" ? 9 : 11} />
      {cfg.label}
    </span>
  );
}

function HistoryDrawer({ testKey }: { testKey: string }) {
  const { data: history = [], isLoading } = useQuery<HistoryEntry[]>({
    queryKey: ["/api/qa/history", testKey],
    queryFn: () => fetch(`/api/qa/history/${testKey}`, { credentials: "include" }).then(r => r.json()),
  });

  if (isLoading) return <div className="py-3 px-4 text-xs text-foreground/40">Loading history…</div>;
  if (!history.length) return <div className="py-3 px-4 text-xs text-foreground/40">No history yet — run the test to see results.</div>;

  return (
    <div className="px-4 py-3 space-y-1.5 border-t border-foreground/[0.06]">
      <p className="text-[10px] font-bold uppercase tracking-widest text-foreground/25 mb-2">Last {history.length} runs</p>
      {history.map((h) => (
        <div key={h.id} className="flex items-center gap-3 text-xs text-foreground/50">
          <StatusBadge status={h.status as QaStatus} size="xs" />
          <span className="text-foreground/30 shrink-0">{h.durationMs}ms</span>
          <span className="flex-1 truncate">{h.message}</span>
          <span className="shrink-0 text-foreground/25">{formatDistanceToNow(new Date(h.ranAt), { addSuffix: true })}</span>
        </div>
      ))}
    </div>
  );
}

function TestRow({
  test,
  onRerun,
  rerunning,
  liveResult,
}: {
  test: QaTest;
  onRerun: (key: string) => void;
  rerunning: boolean;
  liveResult?: LiveRunResult;
}) {
  const [expanded, setExpanded] = useState(false);
  const display: DisplayResult | null = liveResult
    ? toDisplayResult(liveResult)
    : test.latestResult
    ? toDisplayResult(test.latestResult)
    : null;
  const status = display?.status;

  return (
    <>
      <tr
        className="border-b border-foreground/[0.04] hover:bg-foreground/[0.015] transition-colors group"
        data-testid={`test-row-${test.key}`}
      >
        <td className="px-4 py-2.5 text-xs text-foreground/50">{test.category}</td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground/80">{test.name}</span>
            {test.isLive && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-blue-500/10 border border-blue-500/20 text-blue-400">
                <Radio size={8} />
                Live
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-2.5">
          {status ? (
            <StatusBadge status={status} />
          ) : (
            <span className="text-[10px] text-foreground/25">Not run</span>
          )}
        </td>
        <td className="px-4 py-2.5 text-xs text-foreground/40">
          {display ? formatDistanceToNow(new Date(display.ranAt), { addSuffix: true }) : "—"}
        </td>
        <td className="px-4 py-2.5 text-xs text-foreground/40">
          {display ? `${display.durationMs}ms` : "—"}
        </td>
        <td className="px-4 py-2.5 text-xs text-foreground/50 max-w-[260px] truncate" title={display?.message}>
          {display?.message ?? "—"}
        </td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <button
              data-testid={`button-rerun-${test.key}`}
              onClick={() => onRerun(test.key)}
              disabled={rerunning}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium text-foreground/40 hover:text-foreground/70 hover:bg-foreground/[0.05] transition-all disabled:opacity-40"
            >
              <RefreshCw size={11} className={rerunning ? "animate-spin" : ""} />
              {rerunning ? "Running" : "Re-run"}
            </button>
            {test.latestResult && (
              <button
                data-testid={`button-history-${test.key}`}
                onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium text-foreground/40 hover:text-foreground/70 hover:bg-foreground/[0.05] transition-all"
              >
                {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                History
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-foreground/[0.04]">
          <td colSpan={7} className="bg-foreground/[0.01]">
            <HistoryDrawer testKey={test.key} />
          </td>
        </tr>
      )}
    </>
  );
}

function CategorySection({
  category,
  tests,
  onRerun,
  rerunningSingle,
  liveResults,
}: {
  category: string;
  tests: QaTest[];
  onRerun: (key: string) => void;
  rerunningSingle: Set<string>;
  liveResults: Map<string, LiveRunResult>;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const results = tests.map(t => {
    const live = liveResults.get(t.key);
    return live ? toDisplayResult(live) : (t.latestResult ? toDisplayResult(t.latestResult) : null);
  });
  const pass = results.filter(r => r?.status === "pass").length;
  const fail = results.filter(r => r?.status === "fail").length;
  const warn = results.filter(r => r?.status === "warn").length;

  return (
    <div className="mb-2" data-testid={`category-${category.replace(/\s+/g, "-").toLowerCase()}`}>
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center gap-3 px-4 py-2.5 bg-foreground/[0.025] hover:bg-foreground/[0.04] transition-colors rounded-t-xl"
        data-testid={`category-toggle-${category.replace(/\s+/g, "-").toLowerCase()}`}
      >
        {collapsed ? <ChevronRight size={14} className="text-foreground/40" /> : <ChevronDown size={14} className="text-foreground/40" />}
        <span className="font-semibold text-sm text-foreground/80">{category}</span>
        <span className="text-[11px] text-foreground/30">{tests.length} tests</span>
        <div className="ml-auto flex items-center gap-2">
          {pass > 0 && <span className="text-[10px] font-bold text-emerald-400">{pass} pass</span>}
          {fail > 0 && <span className="text-[10px] font-bold text-red-400">{fail} fail</span>}
          {warn > 0 && <span className="text-[10px] font-bold text-amber-400">{warn} warn</span>}
        </div>
      </button>
      {!collapsed && (
        <div className="border border-foreground/[0.06] border-t-0 rounded-b-xl overflow-hidden">
          <table className="w-full">
            <tbody>
              {tests.map(test => (
                <TestRow
                  key={test.key}
                  test={test}
                  onRerun={onRerun}
                  rerunning={rerunningSingle.has(test.key)}
                  liveResult={liveResults.get(test.key)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function QaDashboard() {
  const queryClient = useQueryClient();
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [rerunningSingle, setRerunningSingle] = useState<Set<string>>(new Set());
  const [liveResults, setLiveResults] = useState<Map<string, LiveRunResult>>(new Map());
  const abortRef = useRef<AbortController | null>(null);

  const { data: tests = [], isLoading } = useQuery<QaTest[]>({
    queryKey: ["/api/qa/tests"],
  });

  const categories = Array.from(new Set(tests.map(t => t.category)));

  const allResults = tests.map(t => {
    const live = liveResults.get(t.key);
    return live ? toDisplayResult(live) : (t.latestResult ? toDisplayResult(t.latestResult) : null);
  });
  const passCount = allResults.filter(r => r?.status === "pass").length;
  const failCount = allResults.filter(r => r?.status === "fail").length;
  const warnCount = allResults.filter(r => r?.status === "warn").length;
  const skipCount = allResults.filter(r => r?.status === "skipped").length;
  const runCount = allResults.filter(r => r != null).length;

  const runAll = useCallback(async () => {
    if (running) {
      abortRef.current?.abort();
      setRunning(false);
      return;
    }

    setRunning(true);
    setProgress(0);
    setLiveResults(new Map());

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch("/api/qa/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ dryRun }),
        signal: ctrl.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === "result") {
              setProgress(event.index);
              setTotal(event.total);
              const r = event.result as LiveRunResult;
              setLiveResults(prev => {
                const next = new Map(prev);
                next.set(r.key, { ...r, ranAt: r.ranAt ?? new Date().toISOString() });
                return next;
              });
            }
            if (event.type === "complete") {
              queryClient.invalidateQueries({ queryKey: ["/api/qa/tests"] });
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (e: any) {
      if (e.name !== "AbortError") {
        console.error("[QA] Run failed:", e.message);
      }
    } finally {
      setRunning(false);
      setProgress(0);
    }
  }, [running, dryRun, queryClient]);

  const rerunSingle = useCallback(async (key: string) => {
    setRerunningSingle(prev => new Set(prev).add(key));
    try {
      const res = await fetch(`/api/qa/run/${encodeURIComponent(key)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ dryRun: false }),
      });
      if (res.ok) {
        const result = await res.json() as LiveRunResult;
        setLiveResults(prev => {
          const next = new Map(prev);
          next.set(result.key, { ...result, ranAt: result.ranAt ?? new Date().toISOString() });
          return next;
        });
        queryClient.invalidateQueries({ queryKey: ["/api/qa/tests"] });
        queryClient.invalidateQueries({ queryKey: ["/api/qa/history", key] });
      }
    } finally {
      setRerunningSingle(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [dryRun, queryClient]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-foreground/[0.06] bg-background/50 backdrop-blur-2xl sticky top-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-6 h-14 flex items-center gap-4">
          <Link href="/saas-admin" className="flex items-center gap-2 text-foreground/40 hover:text-foreground/70 transition-colors text-sm">
            <ArrowLeft size={15} />
            SaaS Admin
          </Link>
          <span className="text-foreground/20">/</span>
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-blue-400" />
            <span className="font-bold text-sm text-foreground/80">QA Dashboard</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-dry-run">
              <span className="text-xs text-foreground/50">Dry Run</span>
              <button
                onClick={() => setDryRun(d => !d)}
                className={`relative w-9 h-5 rounded-full transition-colors ${dryRun ? "bg-amber-500/70" : "bg-foreground/20"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dryRun ? "translate-x-4" : ""}`} />
              </button>
              {dryRun && <span className="text-[10px] text-amber-400 font-bold uppercase tracking-wider">On</span>}
            </label>
            <button
              data-testid="button-run-all"
              onClick={runAll}
              disabled={isLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                running
                  ? "bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20"
                  : "bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20"
              }`}
            >
              {running ? (
                <>
                  <span className="animate-pulse">■</span>
                  Stop
                </>
              ) : (
                <>
                  <Play size={14} />
                  Run All Tests
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto px-6 py-6 space-y-6">
        {/* Progress bar */}
        {running && total > 0 && (
          <div className="space-y-1" data-testid="progress-bar-container">
            <div className="flex justify-between text-xs text-foreground/40">
              <span>Running tests…</span>
              <span>{progress}/{total}</span>
            </div>
            <div className="w-full h-1.5 bg-foreground/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-200"
                style={{ width: `${(progress / total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Summary bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="summary-bar">
          {[
            { label: "Pass", count: passCount, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", Icon: CheckCircle },
            { label: "Fail", count: failCount, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", Icon: XCircle },
            { label: "Warn", count: warnCount, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", Icon: AlertTriangle },
            { label: "Skipped", count: skipCount, color: "text-foreground/40", bg: "bg-foreground/5 border-foreground/10", Icon: SkipForward },
          ].map(({ label, count, color, bg, Icon }) => (
            <div key={label} className={`rounded-xl border p-4 flex items-center gap-3 ${bg}`} data-testid={`summary-${label.toLowerCase()}`}>
              <Icon size={20} className={color} />
              <div>
                <div className={`text-2xl font-black ${color}`}>{count}</div>
                <div className="text-xs text-foreground/40">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-4 text-xs text-foreground/35">
          <span data-testid="text-test-count">{tests.length} tests registered</span>
          <span>·</span>
          <span>{runCount} results recorded</span>
          <span>·</span>
          <span data-testid="text-live-count">{tests.filter(t => t.isLive).length} live tests</span>
          {dryRun && <><span>·</span><span className="text-amber-400 font-medium">Dry Run: live tests will be skipped</span></>}
        </div>

        {/* Test table header */}
        <div className="text-xs font-bold uppercase tracking-widest text-foreground/20 grid grid-cols-[180px_1fr_80px_100px_70px_1fr_100px] gap-0 px-4 py-2">
          <span>Category</span>
          <span>Test Name</span>
          <span>Status</span>
          <span>Last Run</span>
          <span>Duration</span>
          <span>Message</span>
          <span>Actions</span>
        </div>

        {/* Category sections */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-foreground/30 text-sm">
            <RefreshCw size={16} className="animate-spin mr-2" />
            Loading test registry…
          </div>
        ) : (
          categories.map(category => (
            <CategorySection
              key={category}
              category={category}
              tests={tests.filter(t => t.category === category)}
              onRerun={rerunSingle}
              rerunningSingle={rerunningSingle}
              liveResults={liveResults}
            />
          ))
        )}
      </div>
    </div>
  );
}
